import React, { useState, useCallback, useEffect, useMemo, ReactNode, useContext, useRef } from 'react';
import { OnboardingContext, OnboardingContextType } from './OnboardingContext';
import UserContext from 'src/contexts/user/UserContext';
import { getCampaigns } from 'src/services/campaign';
import { CopilotChatContext } from 'src/fusion-copilot/context/CopilotChatContext';

interface OnboardingProviderProps {
  children: ReactNode;
}

/**
 * OnboardingProvider
 *
 * Manages onboarding state for new users.
 * All state is session-based (in-memory) - no localStorage.
 * First-time user detection is based on API campaign check.
 */
export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  // Get user context to check for campaigns
  const { account } = useContext(UserContext);

  // Get copilot state to coordinate onboarding with chat
  const { isCopilotOpen } = useContext(CopilotChatContext);

  // Track previous copilot state to detect when it closes
  const prevCopilotOpenRef = useRef<boolean | undefined>(undefined);

  // Session-based state (not persisted)
  const [wizard1Completed, setWizard1CompletedState] = useState(false);
  const [wizard1Skipped, setWizard1SkippedState] = useState(false);
  const [wizard2Shown, setWizard2ShownState] = useState(false);
  // Guard: only true when user explicitly chose "fill manually" in campaign wizard
  const [wizard2DismissedManually, setWizard2DismissedManually] = useState(false);

  // UI state
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showMenuTour, setShowMenuTour] = useState(false);
  const [showGuidedCursor, setShowGuidedCursor] = useState(false);
  const [showCampaignWizard, setShowCampaignWizard] = useState(false);
  const [showCampaignFormTour, setShowCampaignFormTour] = useState(false);
  // Step 2: Designs Tour state
  const [showDesignsTour, setShowDesignsTour] = useState(false);
  const [designsTourStep, setDesignsTourStepState] = useState(0);
  // Step 3: Finalize Tour state
  const [showFinalizeTour, setShowFinalizeTour] = useState(false);
  const [finalizeTourStep, setFinalizeTourStepState] = useState(0);

  // State for tracking if user has campaigns
  const [hasCampaigns, setHasCampaigns] = useState<boolean | null>(null);
  const [campaignsChecked, setCampaignsChecked] = useState(false);

  // Check if user has any campaigns
  useEffect(() => {
    const checkCampaigns = async () => {
      if (!account?.id || campaignsChecked) return;

      try {
        const data = await getCampaigns('');
        const userHasCampaigns = data.results && data.results.length > 0;
        console.log('🧙 [OnboardingProvider] Campaigns check - user has campaigns:', userHasCampaigns, 'count:', data.results?.length);
        setHasCampaigns(userHasCampaigns);
        setCampaignsChecked(true);
      } catch (error) {
        console.error('🧙 [OnboardingProvider] Failed to check campaigns:', error);
        // If API fails, assume no campaigns (show onboarding)
        setCampaignsChecked(true);
        setHasCampaigns(false);
      }
    };

    checkCampaigns();
  }, [account?.id, campaignsChecked]);

  // Computed value: is first-time user (based only on campaigns API check)
  const isFirstTimeUser = useMemo(() => {
    // User is first-time if they have no campaigns and haven't completed/skipped wizard
    if (hasCampaigns === false && !wizard1Completed && !wizard1Skipped) {
      return true;
    }
    return false;
  }, [hasCampaigns, wizard1Completed, wizard1Skipped]);

  // Show welcome modal when copilot closes (only once ever per user, on first registration)
  useEffect(() => {
    // Skip if no account loaded yet
    if (!account?.id) {
      prevCopilotOpenRef.current = isCopilotOpen;
      return;
    }

    // Detect when copilot transitions from open to closed
    const copilotJustClosed = prevCopilotOpenRef.current === true && isCopilotOpen === false;

    // Update the ref for next comparison
    prevCopilotOpenRef.current = isCopilotOpen;

    // Check if this one-time prompt has already been shown for this user
    const storageKey = `fusionads_welcome_tour_prompted_${account.id}`;
    const alreadyPrompted = localStorage.getItem(storageKey) === 'true';

    // Show welcome modal only once ever per user: when copilot closes and hasn't been prompted before
    if (copilotJustClosed && !alreadyPrompted) {
      console.log('🧙 [OnboardingProvider] Copilot closed, showing welcome modal (one-time trigger)');
      localStorage.setItem(storageKey, 'true');
      // Small delay to let the sidebar close animation finish
      const timer = setTimeout(() => {
        setShowWelcomeModal(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isCopilotOpen, account?.id]);

  // Actions for Wizard 1
  const setWizard1Completed = useCallback(() => {
    console.log('🧙 [OnboardingProvider] setWizard1Completed called');
    setWizard1CompletedState(true);
    setShowMenuTour(false);
    setShowGuidedCursor(false);
  }, []);

  const setWizard1Skipped = useCallback(() => {
    console.log('🧙 [OnboardingProvider] setWizard1Skipped called');
    setWizard1SkippedState(true);
    setShowWelcomeModal(false);
    setShowMenuTour(false);
    setShowGuidedCursor(false);
  }, []);

  // Actions for Wizard 2
  const setWizard2Shown = useCallback(() => {
    console.log('🧙 [OnboardingProvider] setWizard2Shown called');
    setWizard2ShownState(true);
  }, []);

  // UI triggers
  const triggerWelcomeModal = useCallback(() => {
    console.log('🧙 [OnboardingProvider] triggerWelcomeModal called');
    setShowWelcomeModal(true);
  }, []);

  const closeWelcomeModal = useCallback(() => {
    console.log('🧙 [OnboardingProvider] closeWelcomeModal called');
    setShowWelcomeModal(false);
  }, []);

  const triggerMenuTour = useCallback((immediate = false) => {
    console.log('🧙 [OnboardingProvider] triggerMenuTour called, immediate:', immediate);
    setShowWelcomeModal(false);
    if (immediate) {
      // Immediate mode: no delay (used when sidebar close animation is already being handled)
      setShowMenuTour(true);
    } else {
      // Normal mode: small delay to let modal close animation finish
      setTimeout(() => {
        setShowMenuTour(true);
      }, 300);
    }
  }, []);

  const closeMenuTour = useCallback(() => {
    console.log('🧙 [OnboardingProvider] closeMenuTour called');
    setShowMenuTour(false);
  }, []);

  const triggerGuidedCursor = useCallback(() => {
    console.log('🧙 [OnboardingProvider] triggerGuidedCursor called');
    setShowMenuTour(false);
    // Ensure menu tour is fully closed before starting cursor
    setTimeout(() => {
      console.log('🧙 [OnboardingProvider] Setting showGuidedCursor to TRUE');
      setShowGuidedCursor(true);
    }, 400);
  }, []);

  const closeGuidedCursor = useCallback(() => {
    console.log('🧙 [OnboardingProvider] closeGuidedCursor called');
    setShowGuidedCursor(false);
  }, []);

  const triggerCampaignWizard = useCallback(() => {
    console.log('🧙 [OnboardingProvider] triggerCampaignWizard called');
    // Reset wizard2Shown so the modal appears
    setWizard2ShownState(false);
    setShowCampaignWizard(true);
  }, []);

  const closeCampaignWizard = useCallback(() => {
    console.log('🧙 [OnboardingProvider] closeCampaignWizard called');
    setShowCampaignWizard(false);
  }, []);

  const triggerCampaignFormTour = useCallback(() => {
    console.log('🧙 [OnboardingProvider] triggerCampaignFormTour called');
    // Set guard flag to indicate user explicitly chose "fill manually"
    setWizard2DismissedManually(true);
    // Close the campaign wizard modal first
    setShowCampaignWizard(false);
    // Small delay to let modal close animation finish
    setTimeout(() => {
      setShowCampaignFormTour(true);
    }, 300);
  }, []);

  const closeCampaignFormTour = useCallback(() => {
    console.log('🧙 [OnboardingProvider] closeCampaignFormTour called');
    setShowCampaignFormTour(false);
  }, []);

  // Step 2: Designs Tour functions
  const triggerDesignsTour = useCallback(() => {
    console.log('🧙 [OnboardingProvider] triggerDesignsTour called');
    setShowDesignsTour(true);
    setDesignsTourStepState(0);
  }, []);

  const closeDesignsTour = useCallback(() => {
    console.log('🧙 [OnboardingProvider] closeDesignsTour called');
    setShowDesignsTour(false);
    setDesignsTourStepState(0);
  }, []);

  const advanceDesignsTour = useCallback(() => {
    console.log('🧙 [OnboardingProvider] advanceDesignsTour called');
    setDesignsTourStepState(prev => prev + 1);
  }, []);

  const setDesignsTourStep = useCallback((step: number) => {
    console.log('🧙 [OnboardingProvider] setDesignsTourStep called:', step);
    setDesignsTourStepState(step);
  }, []);

  // Step 3: Finalize Tour functions
  const triggerFinalizeTour = useCallback(() => {
    console.log('🧙 [OnboardingProvider] triggerFinalizeTour called');
    setShowFinalizeTour(true);
    setFinalizeTourStepState(0);
  }, []);

  const closeFinalizeTour = useCallback(() => {
    console.log('🧙 [OnboardingProvider] closeFinalizeTour called');
    setShowFinalizeTour(false);
    setFinalizeTourStepState(0);
  }, []);

  const advanceFinalizeTour = useCallback(() => {
    console.log('🧙 [OnboardingProvider] advanceFinalizeTour called');
    setFinalizeTourStepState(prev => prev + 1);
  }, []);

  const setFinalizeTourStep = useCallback((step: number) => {
    console.log('🧙 [OnboardingProvider] setFinalizeTourStep called:', step);
    setFinalizeTourStepState(step);
  }, []);

  // Start tour based on current route
  const startTour = useCallback((currentPath: string) => {
    console.log('🧙 [OnboardingProvider] startTour called for path:', currentPath);
    // Check if on campaign page
    const isCampaignPage = currentPath.includes('/projects/campaigns/');

    if (isCampaignPage) {
      // Show Wizard 2 (Campaign Wizard)
      triggerCampaignWizard();
    } else {
      // Show Wizard 1 (Welcome Modal -> Menu Tour)
      // Reset wizard 1 state first
      setWizard1CompletedState(false);
      setWizard1SkippedState(false);
      setShowWelcomeModal(true);
    }
  }, [triggerCampaignWizard]);

  // Reset for "Restart Tour" button
  const resetOnboarding = useCallback(() => {
    console.log('🧙 [OnboardingProvider] resetOnboarding called');
    // Reset Wizard 1 state
    setWizard1CompletedState(false);
    setWizard1SkippedState(false);

    // Reset Wizard 2 state
    setWizard2ShownState(false);
    setWizard2DismissedManually(false);

    // Trigger welcome modal to restart the tour
    setShowWelcomeModal(true);
  }, []);

  // Debug logging for state changes
  useEffect(() => {
    console.log('🧙 [OnboardingProvider] State changed:', {
      showWelcomeModal,
      showMenuTour,
      showGuidedCursor,
      showCampaignWizard,
      showCampaignFormTour,
      showDesignsTour,
      designsTourStep,
      showFinalizeTour,
      finalizeTourStep,
      wizard1Completed,
      wizard1Skipped,
      wizard2Shown,
      wizard2DismissedManually,
      isFirstTimeUser,
      hasCampaigns,
      campaignsChecked,
    });
  }, [showWelcomeModal, showMenuTour, showGuidedCursor, showCampaignWizard, showCampaignFormTour, showDesignsTour, designsTourStep, showFinalizeTour, finalizeTourStep, wizard1Completed, wizard1Skipped, wizard2Shown, wizard2DismissedManually, isFirstTimeUser, hasCampaigns, campaignsChecked]);

  const contextValue: OnboardingContextType = useMemo(
    () => ({
      wizard1Completed,
      wizard1Skipped,
      wizard2Shown,
      wizard2DismissedManually,
      showWelcomeModal,
      showMenuTour,
      showGuidedCursor,
      showCampaignWizard,
      showCampaignFormTour,
      showDesignsTour,
      designsTourStep,
      showFinalizeTour,
      finalizeTourStep,
      setWizard1Completed,
      setWizard1Skipped,
      setWizard2Shown,
      triggerWelcomeModal,
      closeWelcomeModal,
      triggerMenuTour,
      closeMenuTour,
      triggerGuidedCursor,
      closeGuidedCursor,
      triggerCampaignWizard,
      closeCampaignWizard,
      triggerCampaignFormTour,
      closeCampaignFormTour,
      triggerDesignsTour,
      closeDesignsTour,
      advanceDesignsTour,
      setDesignsTourStep,
      triggerFinalizeTour,
      closeFinalizeTour,
      advanceFinalizeTour,
      setFinalizeTourStep,
      resetOnboarding,
      startTour,
      isFirstTimeUser,
    }),
    [
      wizard1Completed,
      wizard1Skipped,
      wizard2Shown,
      wizard2DismissedManually,
      showWelcomeModal,
      showMenuTour,
      showGuidedCursor,
      showCampaignWizard,
      showCampaignFormTour,
      showDesignsTour,
      designsTourStep,
      showFinalizeTour,
      finalizeTourStep,
      setWizard1Completed,
      setWizard1Skipped,
      setWizard2Shown,
      triggerWelcomeModal,
      closeWelcomeModal,
      triggerMenuTour,
      closeMenuTour,
      triggerGuidedCursor,
      closeGuidedCursor,
      triggerCampaignWizard,
      closeCampaignWizard,
      triggerCampaignFormTour,
      closeCampaignFormTour,
      triggerDesignsTour,
      closeDesignsTour,
      advanceDesignsTour,
      setDesignsTourStep,
      triggerFinalizeTour,
      closeFinalizeTour,
      advanceFinalizeTour,
      setFinalizeTourStep,
      resetOnboarding,
      startTour,
      isFirstTimeUser,
    ]
  );

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingProvider;
